import * as turf from '@turf/turf';

// Using dynamic import to load the data only when it's needed
const USAStateData = (() => {
  let data;
  return {
    get: async () => {
      if (!data) data = await import('./data.json');
      return data;
    },
  };
})();

/**
 * Takes a point with `lng` and `lat` properties and returns { name,
 * abbreviation } for the USA state that contains it, or `null` if not found.
 *
 * Uses data from https://github.com/grafana/grafana/blob/7726631fe8f5220fe09f8854616993fc1465bd0e/public/maps/usa-states.geojson
 *
 * NOTE: these state boundaries are low res, so the point may not be in any
 * state if it's near a border or coast. In that case, the function will try
 * expanding the states by 10km and check again.
 */
const getUSAStateForPoint = async ({ lng, lat }) => {
  const data = await USAStateData.get();

  // Find the state that contains the point
  const point = turf.point([lng, lat]);
  let feature = data.features.find((f) => turf.booleanPointInPolygon(point, f));

  // If the point is not in any state, try expanding the states by 10km and check again
  if (!feature) {
    const expandedFeatures = data.features.map((f) => {
      const expanded = turf.buffer(f, 10, { units: 'kilometers' });
      expanded.id = f.id;
      expanded.properties = f.properties;
      return expanded;
    });
    feature = expandedFeatures.find((f) =>
      turf.booleanPointInPolygon(point, f),
    );
  }

  // Return the name of the state and it's abbreviation, or null if not found
  return feature
    ? {
        name: feature.properties.name,
        abbreviation: feature.id,
      }
    : null;
};

export default getUSAStateForPoint;
